import { createContext, ReactElement, useReducer } from "react"
import { BlacklistItem, Track, TracksContextType } from "../types/tracks"
import tracksReducer, { initialTracksState } from "../store/reducers/tracks"
import axios from "utils/axios"
import { FETCH_BLACKLIST, FETCH_TRACKS } from "../store/reducers/actions"
import { mapPaginatedDataToCamelCase, mapToSnakeCase, toSnakeCase } from "../utils/caseTransformation"
import { PaginatedData } from "types/root"
import { getFilterText, toastMessage } from "../utils"
import { Album } from "types/albums"

const TracksContext = createContext<TracksContextType | null>(null)

export const TracksProvider = ({ children }: { children: ReactElement }) => {
  const [state, dispatch] = useReducer(tracksReducer, initialTracksState)

  const fetchTracks = async (
    limit: number,
    offset: number,
    filter?: any,
    orderBy?: string,
    search?: string,
    albumId?: number,
    artistId?: number,
    playlistId?: string
  ) => {
    const tagsIncludedParam = filter.tagsIncluded?.length > 0 ? `&tags_included=${filter.tagsIncluded.join(",")}` : ""
    const tagsExcludedParam = filter.tagsExcluded?.length > 0 ? `&tags_excluded=${filter.tagsExcluded.join(",")}` : ""

    const orderByParam = orderBy ? `&order_by=${orderBy}` : ""
    const searchParam = search ? `&search=${search}` : ""
    const albumIdParam = albumId ? `&album_id=${albumId}` : ""
    const artistIdParam = artistId ? `&artist_id=${artistId}` : ""
    const playlistIdParam = playlistId ? `&playlist_id=${playlistId}` : ""

    const filteredFilter = { ...filter, tagsIncluded: undefined, tagsExcluded: undefined }

    const response = await axios(
      `/tracks/?limit=${limit}&offset=${offset}${getFilterText(
        filteredFilter
      )}${orderByParam}${searchParam}${albumIdParam}${artistIdParam}${playlistIdParam}${tagsIncludedParam}${tagsExcludedParam}`,
      { method: "get" }
    )

    dispatch({
      type: FETCH_TRACKS,
      payload: { ...state, paginatedTracks: mapPaginatedDataToCamelCase<PaginatedData<Track>>(response.data) }
    })
  }

  const fetchBlacklist = async (limit: number, offset: number, search?: string) => {
    const response = await axios(`/black-list/?limit=${limit}&offset=${offset}${search ? `&search=${search}` : ""}`, {
      method: "get"
    })

    dispatch({
      type: FETCH_BLACKLIST,
      payload: {
        ...state,
        paginatedBlacklist: mapPaginatedDataToCamelCase<PaginatedData<BlacklistItem>>(response.data)
      }
    })
  }

  const updateTrack = async (trackToUpdate: Track, keyList: string[]) => {
    const keys = keyList.map((key) => toSnakeCase(key)).join(",")
    const response = await axios(`/tracks/update_status/?keys=${keys}`, {
      method: "put",
      data: mapToSnakeCase(trackToUpdate)
    })

    if (!response.data.success) {
      toastMessage("Error saving track", "error")
      return
    }

    const results = [...state.paginatedTracks.results].map((track: Track) => {
      if (track.id === trackToUpdate.id) {
        for (let i = 0; i < keyList.length; i++) {
          const _key = keyList[i]
          // @ts-ignore
          track[_key] = trackToUpdate[_key]
        }
      }
      return track
    })
    toastMessage("Successfully saved track", "success")

    dispatch({
      type: FETCH_TRACKS,
      payload: {
        ...state,
        paginatedTracks: { ...state.paginatedTracks, results }
      }
    })
  }

  const updateAlbum = async (albumToUpdate: Album, keyList: string[]) => {
    const keys = keyList.map((key) => toSnakeCase(key)).join(",")
    const response = await axios(`/albums/update_key/?keys=${keys}`, {
      method: "put",
      data: mapToSnakeCase(albumToUpdate)
    })

    if (!response.data.success) {
      toastMessage("Error saving track", "error")
      return
    }

    const results = [...state.paginatedTracks.results].map((track: Track) => {
      if (track.albumId === albumToUpdate.id) {
        for (let i = 0; i < keyList.length; i++) {
          const _key = keyList[i]
          // @ts-ignore
          track.album[_key] = albumToUpdate[_key]
        }
      }
      return track
    })
    toastMessage("Successfully saved track", "success")

    dispatch({
      type: FETCH_TRACKS,
      payload: {
        ...state,
        paginatedTracks: { ...state.paginatedTracks, results }
      }
    })
  }

  return (
    <TracksContext.Provider value={{ ...state, fetchTracks, fetchBlacklist, updateTrack, updateAlbum }}>
      {children}
    </TracksContext.Provider>
  )
}

export default TracksContext
